import { type ComponentType, useId } from 'react';

import { Icons } from '@/assets/icons/icons';
import {
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRoot,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu/dropdown-menu';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { cn } from '@/lib/utils';

import type { CalendarChoice } from '@littlebird/apple-calendar/contract';

type IconComponent = ComponentType<{ className?: string }>;

const CHOICES: Record<
  CalendarChoice,
  { label: string; description: string; Icon: IconComponent }
> = {
  meetings: {
    label: 'Meetings and search',
    description: 'Chat and search can read it, and its events become meetings.',
    Icon: Icons.video,
  },
  search: {
    label: 'Search only',
    description: 'Chat and search can read it.',
    Icon: Icons.search,
  },
  hidden: {
    label: 'Hidden',
    description:
      "Littlebird can't see it anywhere and deletes its copy of the events.",
    Icon: Icons.lock,
  },
};

const VIEW_ONLY_REASON =
  'Not available. You can only view this calendar, so it has no meetings of yours.';

interface ChoiceMenuProps {
  name: string;
  value: CalendarChoice | 'mixed';
  onChoose: (choice: CalendarChoice) => void;
  viewOnly: boolean;
  disabled: boolean;
  disabledReason?: string;
}

function ChoiceOption({
  choice,
  selected,
  viewOnly,
  onChoose,
}: {
  choice: CalendarChoice;
  selected: boolean;
  viewOnly: boolean;
  onChoose: (choice: CalendarChoice) => void;
}) {
  const { label, description, Icon } = CHOICES[choice];
  const unavailable = choice === 'meetings' && viewOnly;
  return (
    <DropdownMenuItem
      textSize="xs"
      variant={choice === 'hidden' ? 'destructive' : 'default'}
      role="menuitemradio"
      aria-checked={selected}
      className="items-start"
      disabled={unavailable}
      onSelect={() => onChoose(choice)}
    >
      <span className="size-3.5 shrink-0">
        {selected && <Icons.check className="size-3.5" />}
      </span>
      <Icon className="mt-0.5" />
      <YStack className="min-w-0 flex-1 gap-0.5">
        <span className="text-sm">{label}</span>
        <span className="text-xs text-foreground-muted">
          {unavailable ? VIEW_ONLY_REASON : description}
        </span>
      </YStack>
    </DropdownMenuItem>
  );
}

export function ChoiceMenu({
  name,
  value,
  onChoose,
  viewOnly,
  disabled,
  disabledReason,
}: ChoiceMenuProps) {
  const current = value === 'mixed' ? undefined : CHOICES[value];
  const reasonId = useId();
  const label = current?.label ?? 'Set per calendar';
  const Icon = current?.Icon;
  return (
    <span title={disabledReason} className="shrink-0">
      {disabledReason && (
        <span id={reasonId} className="sr-only">
          {disabledReason}
        </span>
      )}
      <DropdownMenuRoot>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            aria-label={`${name}: ${label}`}
            aria-describedby={disabledReason ? reasonId : undefined}
            className={cn(
              'flex h-7 w-44 items-center gap-1.5 rounded-md border border-border bg-surface px-2 text-xs hover:bg-secondary-hover disabled:pointer-events-none disabled:opacity-60 dark:border-border-strong',
              value === 'hidden' || value === 'mixed'
                ? 'text-foreground-muted'
                : 'text-foreground'
            )}
          >
            {Icon && <Icon className="size-3.5 shrink-0" />}
            <span className="min-w-0 flex-1 truncate text-left">{label}</span>
            <Icons.chevronsUpDown className="size-3 shrink-0 text-foreground-muted" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-80">
          <ChoiceOption
            choice="meetings"
            selected={value === 'meetings'}
            viewOnly={viewOnly}
            onChoose={onChoose}
          />
          <DropdownMenuSeparator />
          <ChoiceOption
            choice="search"
            selected={value === 'search'}
            viewOnly={viewOnly}
            onChoose={onChoose}
          />
          <DropdownMenuSeparator />
          <ChoiceOption
            choice="hidden"
            selected={value === 'hidden'}
            viewOnly={viewOnly}
            onChoose={onChoose}
          />
        </DropdownMenuContent>
      </DropdownMenuRoot>
    </span>
  );
}

export function AccountHeader({
  name,
  expanded,
  onToggle,
  menu,
  muted,
}: {
  name: string;
  expanded: boolean;
  onToggle: () => void;
  menu: React.ReactNode;
  muted: boolean;
}) {
  return (
    <XStack className="items-center gap-3 px-4 py-2.5">
      <button
        type="button"
        aria-expanded={expanded}
        onClick={onToggle}
        className="flex min-w-0 flex-1 items-center gap-2 text-left"
      >
        <Icons.chevronDown
          className={cn(
            'size-3 shrink-0 text-foreground-muted',
            !expanded && '-rotate-90'
          )}
        />
        <span
          className={cn(
            'truncate text-sm font-medium',
            muted ? 'text-foreground-muted' : 'text-foreground'
          )}
        >
          {name}
        </span>
      </button>
      {menu}
    </XStack>
  );
}

export function CalendarRow({
  name,
  color,
  menu,
}: {
  name: string;
  color?: string;
  menu: React.ReactNode;
}) {
  return (
    <XStack className="items-center gap-3 py-1.5 pl-10 pr-4 last:pb-2.5">
      <span
        className="size-1.5 shrink-0 rounded-full bg-foreground-subtle"
        style={{ backgroundColor: color }}
      />
      <span className="min-w-0 flex-1 truncate text-sm text-foreground">
        {name}
      </span>
      {menu}
    </XStack>
  );
}
